/**
 * The Mine shelf's two small rules, kept out of `discover-view.tsx` so they
 * can be tested without mounting the 1,500-line surface.
 *
 * Both fix what the 2026-09-26 e2e run saw on Discover › Agents › Mine
 * (handoff 2026-09-26, "Portal UX"):
 *
 *   - "No results found" flashed before the shelf had loaded. The shelves
 *     start as `null` (never fetched) and the grid read `null` as an empty
 *     list, so the empty-state copy showed for the whole round trip.
 *   - Cards showed the handle where the name belongs. `/api/agents` returns
 *     `displayName`; the mapper read `row.name`, which that route has never
 *     sent, and fell through to the username.
 */

export type MineShelfRoot = 'all' | 'apps' | 'agents';

/**
 * True while Mine is on and a shelf the current root shows has not landed
 * yet. `null` is "never fetched"; an empty array is a real empty shelf. A
 * re-fetch for a new query keeps the previous rows on screen, so this only
 * covers the first load after Mine is switched on.
 */
export function isMineShelfLoading(s: {
  mineOnly: boolean;
  root: MineShelfRoot;
  mineApps: unknown[] | null;
  mineAgents: unknown[] | null;
}): boolean {
  if (!s.mineOnly) return false;
  const wantApps = s.root !== 'agents';
  const wantAgents = s.root !== 'apps';
  return (wantApps && s.mineApps === null) || (wantAgents && s.mineAgents === null);
}

/**
 * The name a Mine-shelf agent card shows: the display name, then a legacy
 * `name` field should any older response carry one, then the handle. Never
 * blank: the handle is always present on a `users` row.
 */
export function mineAgentName(row: Record<string, unknown>): string {
  const username = String(row.username ?? '');
  const display = typeof row.displayName === 'string' ? row.displayName.trim() : '';
  const legacy = typeof row.name === 'string' ? row.name.trim() : '';
  return display || legacy || username;
}
